import React, { useState, useEffect } from 'react';
import { Settings, Plus, Grid3X3, Bookmark, Banknote, Play, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import BottomNavigation from '@/components/BottomNavigation';
import FollowersModal from '@/components/FollowersModal';
import PostViewModal from '@/components/PostViewModal';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';

interface Profile {
  id: string;
  full_name: string;
  is_verified: boolean;
  role: string;
  posts_count: number;
  followers_count: number;
  following_count: number;
  avatar_url: string;
  available_earnings: number;
}

interface Post {
  id: string;
  user_id: string;
  content_type: string;
  media_urls: string[];
  thumbnail_url?: string;
  caption: string | null;
  likes_count: number;
  comments_count: number;
  saves_count: number;
  created_at: string;
  profiles: {
    full_name: string;
    role: string;
    avatar_url: string;
    is_verified: boolean;
  } | null;
  restaurants: {
    id: string;
    name: string;
  } | null;
  menu_items: {
    id: string;
    name: string;
    price: number;
    image_url: string;
  } | null;
}

const ProfilePage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userPosts, setUserPosts] = useState<Post[]>([]);
  const [savedPosts, setSavedPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletePost, setDeletePost] = useState<Post | null>(null);
  const [followersModal, setFollowersModal] = useState<'followers' | 'following' | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  
  const targetUserId = userId || user?.id;
  const isOwnProfile = targetUserId === user?.id;

  useEffect(() => {
    if (targetUserId) {
      fetchProfile();
      fetchUserPosts();
      if (isOwnProfile) {
        fetchSavedPosts();
      }
      if (user && !isOwnProfile) {
        checkFollowStatus();
      }
    }
  }, [targetUserId, user, isOwnProfile]);

  const fetchProfile = async () => {
    if (!targetUserId) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', targetUserId)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosts = async () => {
    if (!targetUserId) return;
    
    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          profiles(full_name, role, avatar_url, is_verified),
          restaurants(id, name),
          menu_items(id, name, price, image_url)
        `)
        .eq('user_id', targetUserId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserPosts(data || []);
    } catch (error) {
      console.error('Error fetching user posts:', error);
    }
  };

  const fetchSavedPosts = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('post_saves')
        .select(`
          post_id,
          posts!inner(
            *,
            profiles(full_name, role, avatar_url, is_verified),
            restaurants(id, name),
            menu_items(id, name, price, image_url)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedPosts(data?.map(item => item.posts).filter(Boolean) || []);
    } catch (error) {
      console.error('Error fetching saved posts:', error);
    }
  };

  const handleDeletePost = async () => {
    if (!deletePost || !user) return;

    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', deletePost.id)
        .eq('user_id', user.id);

      if (error) throw error;

      setUserPosts(prev => prev.filter(post => post.id !== deletePost.id));
      setDeletePost(null);
      
      toast({
        title: "Post deleted",
        description: "Your post has been deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive",
      });
    }
  };

  const checkFollowStatus = async () => {
    if (!user || !targetUserId || isOwnProfile) return;
    
    try {
      const { data, error } = await supabase
        .from('user_follows')
        .select('id')
        .eq('follower_id', user.id)
        .eq('following_id', targetUserId)
        .single();

      setIsFollowing(!!data);
    } catch (error) {
      setIsFollowing(false);
    }
  };

  const handleFollow = async () => {
    if (!user || !targetUserId || isOwnProfile) return;

    try {
      if (isFollowing) {
        await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', targetUserId);
        setIsFollowing(false);
      } else {
        await supabase
          .from('user_follows')
          .insert({ follower_id: user.id, following_id: targetUserId });
        setIsFollowing(true);
      }
      fetchProfile(); // Refresh profile to update follower count
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
  };

  const handlePostClick = (post: Post) => {
    setSelectedPost(post);
  };

  const handleDeleteFromModal = async (post: Post) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', post.id)
        .eq('user_id', user.id);

      if (error) throw error;

      setUserPosts(prev => prev.filter(p => p.id !== post.id));
      setSavedPosts(prev => prev.filter(p => p.id !== post.id));
      
      toast({
        title: "Post deleted",
        description: "Your post has been deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive",
      });
    }
  };

  const PostGrid = ({ posts, isOwnPosts = false }: { posts: Post[]; isOwnPosts?: boolean }) => (
    <div className="grid grid-cols-3 gap-1">
      {posts.map((post) => (
        <div 
          key={post.id} 
          className="relative aspect-square bg-gray-200 group cursor-pointer"
          onClick={() => handlePostClick(post)}
        >
          {post.content_type === 'video' ? (
            <>
              <img
                src={post.thumbnail_url || post.media_urls[0]}
                alt="Video thumbnail"
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to actual video if thumbnail fails
                  const target = e.target as HTMLImageElement;
                  const video = document.createElement('video');
                  video.src = post.media_urls[0];
                  video.className = "w-full h-full object-cover";
                  video.muted = true;
                  target.parentNode?.replaceChild(video, target);
                }}
              />
              <Play className="absolute top-2 right-2 h-4 w-4 text-white" />
            </>
          ) : (
            <img
              src={post.media_urls[0]}
              alt="Post"
              className="w-full h-full object-cover"
            />
          )}
        </div>
      ))}
    </div>
  );

  const EmptyState = ({ message }: { message: string }) => (
    <div className="flex flex-col items-center justify-center py-16 text-gray-500">
      <Grid3X3 className="h-12 w-12 mb-4 text-gray-300" />
      <p className="text-sm">{message}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white">
        <div className="px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold">{profile.full_name}</h1>
              {profile.is_verified && (
                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
            </div>
            {isOwnProfile ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/settings')}
              >
                <Settings className="h-6 w-6" />
              </Button>
            ) : (
              <Button
                onClick={handleFollow}
                className={`px-6 py-2 rounded-lg ${
                  isFollowing 
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </Button>
            )}
          </div>

          {/* Profile Header */}
          <div className="bg-white border-b px-4 py-6">
            <div className="max-w-md mx-auto">
              {/* Profile Picture and Name */}
              <div className="flex flex-col items-center mb-6">
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center overflow-hidden mb-3">
                  {profile.avatar_url ? (
                    <img 
                      src={profile.avatar_url} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-2xl font-bold">
                      {profile.full_name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold text-lg">{profile.full_name}</h2>
                  {profile.is_verified && (
                    <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
                
                {isOwnProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => navigate('/edit-profile')}
                  >
                    Edit Profile
                  </Button>
                )}
              </div>
              
              {/* Stats Row */}
              <div className="flex justify-center items-center gap-8">
                <div className="text-center flex flex-col items-center">
                  <div className="text-2xl font-bold">{profile.posts_count}</div>
                  <div className="text-xs text-gray-500">Posts</div>
                </div>
                <button 
                  className={`text-center p-2 rounded transition-colors flex flex-col items-center ${
                    isOwnProfile ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                  }`}
                  onClick={() => isOwnProfile && setFollowersModal('followers')}
                >
                  <div className="text-2xl font-bold">{profile.followers_count}</div>
                  <div className="text-xs text-gray-500">Followers</div>
                </button>
                <button 
                  className={`text-center p-2 rounded transition-colors flex flex-col items-center ${
                    isOwnProfile ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                  }`}
                  onClick={() => isOwnProfile && setFollowersModal('following')}
                >
                  <div className="text-2xl font-bold">{profile.following_count}</div>
                  <div className="text-xs text-gray-500">Following</div>
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4">
            <Tabs defaultValue={searchParams.get('tab') || 'posts'} className="w-full">
              <TabsList className={`grid w-full ${isOwnProfile ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <TabsTrigger value="posts" className="flex items-center gap-2">
                  <Grid3X3 className="h-4 w-4" />
                  Posts
                </TabsTrigger>
                {isOwnProfile && (
                  <TabsTrigger value="saved" className="flex items-center gap-2">
                    <Bookmark className="h-4 w-4" />
                    Saved
                  </TabsTrigger>
                )}
              </TabsList>
              
              <TabsContent value="posts" className="mt-0">
                {userPosts.length === 0 ? (
                  <EmptyState message="No posts yet" />
                ) : (
                  <PostGrid posts={userPosts} isOwnPosts={isOwnProfile} />
                )}
              </TabsContent>
              {isOwnProfile && (
                <TabsContent value="saved" className="mt-0">
                  {savedPosts.length === 0 ? (
                    <EmptyState message="No saved posts yet" />
                  ) : (
                    <PostGrid posts={savedPosts} isOwnPosts={false} />
                  )}
                </TabsContent>
              )}
            </Tabs>
          </div>
        </div>
      </div>

      <BottomNavigation />

      {/* Followers/Following Modal */}
      {followersModal && targetUserId && (
        <FollowersModal 
          isOpen={!!followersModal}
          onClose={() => setFollowersModal(null)}
          userId={targetUserId}
          type={followersModal}
        />
      )}

      {/* Post View Modal */}
      {selectedPost && (
        <PostViewModal
          isOpen={!!selectedPost}
          onClose={() => setSelectedPost(null)}
          post={selectedPost}
          onBack={() => setSelectedPost(null)}
          showDeleteButton={selectedPost.user_id === user?.id}
          onDelete={handleDeleteFromModal}
        />
      )}

      {/* Delete Post Dialog */}
      {deletePost && (
        <Dialog open={!!deletePost} onOpenChange={() => setDeletePost(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Post</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this post? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeletePost(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeletePost}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ProfilePage;